import React, { useState } from 'react';
import { axiosInstance } from '../../App';
import { Loader2 } from 'lucide-react';

/**
 * Change-Registered-Email modal, shared between Admin → Users and Business
 * Management. All the sensitive logic lives on the backend
 * (`POST /api/admin/users/:id/change-email`) — the modal only collects the
 * new email + verification reason and calls the endpoint.
 *
 * Props
 *   open, onClose      – modal controls
 *   target             – { id, email, label? } — describes WHO we're editing
 *   onCompleted(data)  – callback with the full response (includes
 *                        resetDelivery) so the caller can toast + refresh.
 *   onError(message)   – callback with error text so caller can toast.
 */
const ChangeEmailModal = ({ open, target, onClose, onCompleted, onError }) => {
  const [form, setForm] = useState({ newEmail: '', reason: '' });
  const [busy, setBusy] = useState(false);

  if (!open || !target) return null;

  const submit = async () => {
    const newEmail = (form.newEmail || '').trim().toLowerCase();
    const reason = (form.reason || '').trim();
    if (!newEmail || !newEmail.includes('@')) { onError?.('Enter a valid new email'); return; }
    if (reason.length < 10) { onError?.('Reason must explain how ownership was verified (min 10 chars)'); return; }
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/admin/users/${target.id}/change-email`, { newEmail, reason });
      onCompleted?.(r.data);
      setForm({ newEmail: '', reason: '' });
      onClose?.();
    } catch (err) {
      onError?.(err.response?.data?.detail || 'Failed to change email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="change-email-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-gray-900">Change registered login email</h3>
        {target.label && <p className="text-xs text-gray-500 mt-0.5">{target.label}</p>}
        <p className="text-sm text-gray-600 mt-1">
          Current: <strong className="font-mono">{target.email}</strong>
        </p>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
          This will invalidate every existing session, warn the old email address,
          and email a password-reset link to the new one. All actions are audited.
        </p>
        <label className="block mt-4 text-xs font-semibold text-gray-700">New email address</label>
        <input
          type="email"
          value={form.newEmail}
          onChange={(e) => setForm(f => ({ ...f, newEmail: e.target.value }))}
          placeholder="new@example.com"
          className="w-full mt-1 p-2.5 border border-gray-300 rounded-lg text-sm"
          data-testid="change-email-new-input"
          autoFocus
        />
        <label className="block mt-3 text-xs font-semibold text-gray-700">
          How was ownership verified? <span className="text-red-600">*</span>
        </label>
        <textarea
          rows={3}
          value={form.reason}
          onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
          placeholder="e.g. Called owner on the phone number listed on the business + confirmed last invoice number"
          maxLength={500}
          className="w-full mt-1 p-2.5 border border-gray-300 rounded-lg text-sm resize-none"
          data-testid="change-email-reason-input"
        />
        <p className="text-[11px] text-gray-500 mt-1">Minimum 10 characters. This is recorded in the audit log.</p>
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => { setForm({ newEmail: '', reason: '' }); onClose?.(); }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            data-testid="change-email-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="change-email-confirm"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Change email
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeEmailModal;

/**
 * sendAdminPasswordReset — one-shot helper both screens use for the Send
 * Reset button. Returns the response so the caller can inspect
 * `delivered` / `deliveryStatus` and build an honest toast.
 */
export async function sendAdminPasswordReset(userId) {
  const r = await axiosInstance.post(`/admin/users/${userId}/send-password-reset`);
  return r.data || {};
}

/**
 * toastForReset — turns a reset-response into { level, message } so both
 * screens produce identical, delivery-accurate toasts.
 */
export function toastForReset(data, fallbackTarget) {
  if (data?.delivered) {
    return {
      level: 'success',
      message: `Reset email accepted by the provider for ${data.sentTo || fallbackTarget} (id: ${data.emailProviderId || 'n/a'})`,
    };
  }
  if (data?.deliveryStatus === 'logged') {
    return {
      level: 'error',
      message: `Reset TOKEN created but the email service is not configured — no email was actually sent. Provider msg: ${data.providerResponse || 'none'}`,
    };
  }
  return {
    level: 'error',
    message: `Reset TOKEN created but delivery FAILED (${data?.deliveryStatus || 'unknown'}): ${data?.providerResponse || 'no provider response'}`,
  };
}
